"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@workspace/ui/lib/utils";
import { icons } from "@workspace/ui/icons";
import { Button } from "@workspace/ui/components/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@workspace/ui/components/navigation-menu";
import {
  DESKTOP_NAV_ITEMS,
  MOBILE_NAV_ITEMS,
  CAREERS_NAV_ITEM,
} from "../_utils/navbar";
import { useApp } from "@/components/providers/app-provider";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

interface NavbarProps {
  transparent?: boolean;
  hasBanner?: boolean;
  showOverlayCta?: boolean;
  hideOnScroll?: boolean;
  hideCta?: boolean;
  /** When true, mobile full-screen menu uses solid scrim only (no backdrop blur). */
  noMobileMenuBlur?: boolean;
}

export default function Navbar({
  transparent = false,
  hasBanner = false,
  showOverlayCta = false,
  hideOnScroll = false,
  hideCta = false,
  noMobileMenuBlur = false,
}: NavbarProps): React.ReactElement {
  const pathname = usePathname() ?? "";
  const isUsecaseNav = pathname.startsWith("/how-inito-works");
  const { region } = useApp();
  const showCareers = region !== "us";
  const desktopNavItems = showCareers
    ? [...DESKTOP_NAV_ITEMS, CAREERS_NAV_ITEM]
    : DESKTOP_NAV_ITEMS;
  const mobileNavItems = showCareers
    ? [...MOBILE_NAV_ITEMS, CAREERS_NAV_ITEM]
    : MOBILE_NAV_ITEMS;

  let tryInitoSectionType: string;
  if (pathname === "/buy-now") {
    tryInitoSectionType = SectionTypes.PRODUCT_SELECTION;
  } else if (isUsecaseNav) {
    tryInitoSectionType = SectionTypes.USE_CASE;
  } else {
    tryInitoSectionType = SectionTypes.HERO_BANNER;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [openMobileAccordion, setOpenMobileAccordion] = useState<string | null>(
    null,
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const Logo = icons["logo"];
  const ChevronDown = icons["icon-chevron-down"];
  const IconMenu = icons["icon-menu"];
  const IconClose = icons["icon-close-big"];

  // When transparent, track scroll to revert to the solid white header
  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setIsScrolled(window.scrollY > 2);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  // Hide on scroll-down (mobile only), reveal on scroll-up or when scroll stops.
  // Does not trigger within the first 80px of the page to avoid jitter near the top.
  useEffect(() => {
    if (!hideOnScroll) return;
    const MIN_DELTA = 5;
    const TOP_THRESHOLD = 80;
    const STOP_DELAY = 200;
    const LG_BREAKPOINT = 1024;
    const onScroll = () => {
      // Behaviour is mobile-only; the CSS class max-lg:-translate-y-full is
      // already a no-op on desktop, but we skip state updates entirely to
      // avoid unnecessary re-renders on larger viewports.
      if (window.innerWidth >= LG_BREAKPOINT) return;
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;
      if (Math.abs(diff) < MIN_DELTA) return;
      if (currentY < TOP_THRESHOLD) {
        setIsHidden(false);
      } else if (diff > 0) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      lastScrollY.current = currentY;
      // Reveal navbar shortly after scrolling stops
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      stopTimerRef.current = setTimeout(() => setIsHidden(false), STOP_DELAY);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    };
  }, [hideOnScroll]);

  // Reset accordion when the mobile menu is closed
  useEffect(() => {
    if (!isOpen) setOpenMobileAccordion(null);
  }, [isOpen]);

  // True only while the transparent variant is requested AND the user hasn't scrolled
  const isTransparentActive = transparent && !isScrolled && !isOpen;

  const toggleMobileAccordion = (label: string) => {
    setOpenMobileAccordion((prev) => (prev === label ? null : label));
  };

  return (
    <>
      {/* ── Mobile full-screen overlay ── */}
      {isOpen && (
        <div
          className={cn(
            // z-[51] to stay above the floating quiz card (z-50)
            "fixed inset-0 z-[51] flex flex-col overflow-hidden overscroll-none lg:hidden",
            !noMobileMenuBlur && "backdrop-blur-[21px]",
          )}
          style={{ background: "rgba(0, 0, 0, 0.80)" }}
        >
          {/* Overlay header bar */}
          <div className="flex items-center justify-between px-5 py-[17px]">
            <Link
              id="navbar-mobile-drawer-logo-link"
              data-testid="navbar-mobile-drawer-logo-link"
              href="/"
              aria-label="Inito home"
              onClick={() => setIsOpen(false)}
            >
              {Logo && <Logo className="h-[29px] w-20 text-white" />}
            </Link>
            <div className="flex items-center gap-4">
              {showOverlayCta && !hideCta && (
                <Button
                  id="navbar-try-inito-drawer-cta"
                  data-testid="navbar-try-inito-drawer-cta"
                  className="h-9 rounded-[10px] px-4 py-2 title-14 bg-white text-primary hover:bg-white/90 hover:cursor-pointer"
                  asChild
                >
                  <Link
                    id="navbar-try-inito-drawer-cta-link"
                    data-testid="navbar-try-inito-drawer-cta-link"
                    href="/buy-now"
                    onClick={() => {
                      sendEvent(
                        EventNames.TRY_INITO_CLICKED,
                        withSectionType(tryInitoSectionType),
                      );
                      sendEvent(
                        EventNames.SIDE_DRAWER_ITEM_CLICKED,
                        withSectionType(SectionTypes.DRAWER, {
                          item_name: "Try Inito",
                        }),
                      );
                      setIsOpen(false);
                    }}
                  >
                    Try Inito
                  </Link>
                </Button>
              )}
              <button
                id="navbar-mobile-drawer-close"
                data-testid="navbar-mobile-drawer-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close menu"
                className="flex items-center justify-center text-white"
              >
                {IconClose && <IconClose className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Menu body */}
          <div className="flex flex-1 flex-col justify-between overflow-y-auto px-6 pb-6 pt-10">
            <nav className="flex flex-col gap-6">
              {mobileNavItems.map((item, itemIndex) => {
                const isExpanded = openMobileAccordion === item.label;
                const itemNumber = itemIndex + 1;
                return (
                  <div key={item.label} className="flex flex-col gap-4">
                    {/* Row: label + chevron */}
                    {item.hasDropdown ? (
                      <button
                        id={`navbar-mobile-item-${itemNumber}-trigger`}
                        data-testid={`navbar-mobile-item-${itemNumber}-trigger`}
                        onClick={() => toggleMobileAccordion(item.label)}
                        className="flex w-full items-center gap-1 text-left"
                      >
                        <span className="title-24 text-white">
                          {item.label}
                        </span>
                        <span className="flex items-center">
                          {ChevronDown && (
                            <ChevronDown
                              className={cn(
                                "h-[18px] w-[18px] shrink-0 text-white transition-transform duration-200",
                                isExpanded && "rotate-180",
                              )}
                            />
                          )}
                        </span>
                      </button>
                    ) : (
                      <Link
                        id={`navbar-mobile-item-${itemNumber}`}
                        data-testid={`navbar-mobile-item-${itemNumber}`}
                        href={item.href}
                        className="title-24 text-white"
                        onClick={() => {
                          sendEvent(
                            EventNames.SIDE_DRAWER_ITEM_CLICKED,
                            withSectionType(SectionTypes.DRAWER, {
                              item_name: item.label,
                            }),
                          );
                          setIsOpen(false);
                        }}
                      >
                        {item.label}
                      </Link>
                    )}

                    {/* Expanded card */}
                    {item.hasDropdown && isExpanded && item.dropdownItems && (
                      <div className="rounded-xl bg-white/5 p-5">
                        <div className="flex flex-col gap-5">
                          {item.dropdownItems.map((sub, idx) => (
                            <React.Fragment key={sub.label}>
                              {idx > 0 && (
                                <div className="h-px w-full bg-white/10" />
                              )}
                              <Link
                                id={`navbar-mobile-dropdown-item-${itemNumber}-${idx + 1}`}
                                data-testid={`navbar-mobile-dropdown-item-${itemNumber}-${idx + 1}`}
                                href={sub.href}
                                onClick={() => {
                                  sendEvent(
                                    EventNames.SIDE_DRAWER_ITEM_CLICKED,
                                    withSectionType(SectionTypes.DRAWER, {
                                      item_name: sub.label,
                                    }),
                                  );
                                  setIsOpen(false);
                                }}
                                className="title-18 text-[#F3F2F1]"
                              >
                                {sub.label}
                              </Link>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* ── Main header bar ── */}
      <header
        className={cn(
          "z-40 w-full transition-all duration-300",
          transparent ? "fixed" : "sticky",
          hasBanner ? "top-12" : "top-0",
          isTransparentActive
            ? isUsecaseNav
              ? "bg-[rgba(0,0,0,0.3)]"
              : "bg-transparent"
            : "bg-white",
          isOpen && "hidden lg:block",
          isHidden && "max-lg:-translate-y-full ",
          "px-5 py-4 lg:px-20 lg:py-4",
        )}
      >
        <div className="mx-auto flex items-center max-w-[1440px] justify-between ">
          {/* Logo */}
          <Link
            id="navbar-logo-link"
            data-testid="navbar-logo-link"
            href="/"
            aria-label="Inito home"
            className="flex shrink-0 items-center"
          >
            {Logo && (
              <Logo
                className={cn(
                  "h-[29px] w-20 lg:h-9 lg:w-[97px] transition-colors duration-300",
                  isTransparentActive ? "text-white" : "text-primary",
                )}
              />
            )}
          </Link>

          {/* Mobile right side — hamburger + Try Inito (unless hidden) */}
          <div className="flex items-center gap-4 lg:hidden">
            {!hideCta && (
              <Button
                id="navbar-try-inito-mobile-cta"
                data-testid="navbar-try-inito-mobile-cta"
                className={cn(
                  "h-9 rounded-[10px] px-4 py-2 title-14 hover:cursor-pointer transition-colors duration-300",
                  transparent && isTransparentActive
                    ? "bg-white text-primary hover:bg-white/90"
                    : "bg-primary text-white hover:bg-primary/90",
                )}
                asChild
              >
                <Link
                  id="navbar-try-inito-mobile-cta-link"
                  data-testid="navbar-try-inito-mobile-cta-link"
                  href="/buy-now"
                  onClick={() =>
                    sendEvent(
                      EventNames.TRY_INITO_CLICKED,
                      withSectionType(tryInitoSectionType),
                    )
                  }
                >
                  Try Inito
                </Link>
              </Button>
            )}
            <button
              id="navbar-mobile-menu-toggle"
              data-testid="navbar-mobile-menu-toggle"
              onClick={() => {
                setIsOpen((wasOpen) => {
                  if (!wasOpen) {
                    sendEvent(
                      EventNames.DRAWER_CLICKED,
                      withSectionType(SectionTypes.DRAWER),
                    );
                  }
                  return true;
                });
                setIsHidden(false);
              }}
              aria-label="Open menu"
              className={cn(
                "flex items-center justify-center transition-colors duration-300",
                isTransparentActive ? "text-white" : "text-primary",
              )}
            >
              {IconMenu && <IconMenu className="h-6 w-6" />}
            </button>
          </div>

          {/* Desktop nav */}
          <div className="hidden items-center gap-12 lg:flex">
            <NavigationMenu viewport={false} className="flex-none">
              <NavigationMenuList className="gap-12">
                {desktopNavItems.map((item, itemIndex) => {
                  const itemNumber = itemIndex + 1;
                  return (
                  <NavigationMenuItem key={item.label}>
                    {item.hasDropdown ? (
                      <>
                        <NavigationMenuTrigger
                          id={`navbar-desktop-item-${itemNumber}-trigger`}
                          data-testid={`navbar-desktop-item-${itemNumber}-trigger`}
                          className={cn(
                            "h-auto rounded-none px-0 py-0 title-16 shadow-none [&>svg]:size-4",
                            "bg-transparent hover:bg-transparent focus:bg-transparent",
                            "data-[state=open]:bg-transparent data-[state=open]:shadow-none",
                            "data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent",
                            "focus-visible:ring-0 focus-visible:outline-none",
                            isTransparentActive
                              ? "text-white hover:text-white focus:text-white data-[state=open]:text-white"
                              : "text-primary hover:text-primary focus:text-primary data-[state=open]:text-primary",
                          )}
                        >
                          {item.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="p-0">
                          <ul
                            className={cn(
                              "flex flex-col p-2",
                              item.dropdownClassName,
                            )}
                          >
                            {item.dropdownItems?.map((sub, subIndex) => (
                              <li key={sub.label}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    id={`navbar-desktop-dropdown-item-${itemNumber}-${subIndex + 1}`}
                                    data-testid={`navbar-desktop-dropdown-item-${itemNumber}-${subIndex + 1}`}
                                    href={sub.href}
                                    className="block whitespace-nowrap rounded-[10px] px-3 py-2.5 title-16 text-primary transition-colors hover:bg-[#f3f2f1]"
                                    onClick={() =>
                                      sendEvent(
                                        EventNames.SIDE_DRAWER_ITEM_CLICKED,
                                        withSectionType(SectionTypes.DRAWER, {
                                          item_name: sub.label,
                                        }),
                                      )
                                    }
                                  >
                                    {sub.label}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <Link
                        id={`navbar-desktop-item-${itemNumber}`}
                        data-testid={`navbar-desktop-item-${itemNumber}`}
                        href={item.href}
                        className={cn(
                          "title-16 transition-colors",
                          isTransparentActive
                            ? "text-white hover:text-white/70"
                            : "text-primary hover:text-primary/70",
                        )}
                        onClick={() =>
                          sendEvent(
                            EventNames.SIDE_DRAWER_ITEM_CLICKED,
                            withSectionType(SectionTypes.DRAWER, {
                              item_name: item.label,
                            }),
                          )
                        }
                      >
                        {item.label}
                      </Link>
                    )}
                  </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>

            {!hideCta && (
              <Button
                id="navbar-try-inito-desktop-cta"
                data-testid="navbar-try-inito-desktop-cta"
                className={cn(
                  "rounded-xl px-5 py-[13px] title-16 hover:cursor-pointer transition-colors duration-300",
                  isTransparentActive
                    ? "bg-white text-primary hover:bg-white/90"
                    : "bg-primary text-white hover:bg-primary/90",
                )}
                asChild
              >
                <Link
                  id="navbar-try-inito-desktop-cta-link"
                  data-testid="navbar-try-inito-desktop-cta-link"
                  href="/buy-now"
                  onClick={() =>
                    sendEvent(
                      EventNames.TRY_INITO_CLICKED,
                      withSectionType(tryInitoSectionType),
                    )
                  }
                >
                  Try Inito
                </Link>
              </Button>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
